/**
 * OrganizationDomainSuggestion - inline "we found a match" prompt shown while
 * creating a contact. When the typed email's domain (e.g. fred@acme.com)
 * matches an existing organization's domain or website, we surface that
 * organization with one click to attach it — saving the user from re-keying
 * a company they already have.
 */
import { useTranslation } from 'react-i18next';
import { Building2, Link2, X } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import type { Organization } from '../types';

interface OrganizationDomainSuggestionProps {
  organization: Organization;
  /** The email domain that produced the match (e.g. "acme.com"). */
  domain: string;
  /** Attach the suggested organization to the contact. */
  onUse: (organization: Organization) => void;
  /** Dismiss the suggestion for the current domain. */
  onDismiss: () => void;
}

export function OrganizationDomainSuggestion({
  organization,
  domain,
  onUse,
  onDismiss,
}: OrganizationDomainSuggestionProps) {
  const { t } = useTranslation(['contacts', 'common']);

  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-lg border border-info/30 bg-info/5 p-2.5"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-info/10 text-info">
        <Building2 className="h-5 w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-info">
          {t('contacts.domainSuggestion.caption', { domain })}
        </p>
        <p className="truncate text-sm font-medium text-foreground">{organization.name}</p>
      </div>

      <Button
        type="button"
        size="sm"
        variant="default"
        className="shrink-0 gap-1.5"
        onClick={() => onUse(organization)}
      >
        <Link2 className="h-4 w-4" />
        {t('common:app.add')}
      </Button>

      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-11 w-11 shrink-0 text-muted-foreground"
        onClick={onDismiss}
        aria-label={t('common:app.dismiss')}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
